import {
  type LiHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
  type VFC,
  forwardRef,
  useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Color, Theme } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { WithStyleOverrides } from '../../../gem'
import { Avatar } from '../../Avatar'
import { Checkbox } from '../../Checkbox'
import { ColorDotToken } from '../../ColorToken'
import { Icon } from '../../Icon'
import * as styles from '../styles'

export enum MultiSelectType {
  radio = 'radio',
  checkbox = 'checkbox',
}

export type MenuOptionIcon = {
  glyph: string
  visibility: 'always' | 'selected' | 'disabled'
  color?: string
  useHypernova?: boolean
}

type ShowOptionIconArg = {
  highlightIcon?: boolean
  icon?: MenuOptionIcon
  disabled?: boolean
  selected?: boolean
  theme?: Theme
  ariaHidden?: boolean
}

type MenuOptionProps<IdValue> = Readonly<
  WithStyleOverrides &
    Omit<LiHTMLAttributes<HTMLLIElement>, 'id'> & {
      color?: string | null
      colorsEnabled?: boolean
      disabled?: boolean
      leadingAvatarProps?: {
        name: string
        fullUrl?: string
      }
      leadingIcon?: MenuOptionIcon
      highlightedByKeyboard?: boolean
      id: IdValue
      multiSelectType?: MultiSelectType
      onSelect?: (selected: { id: IdValue; optionText: string }) => void
      optionText: string
      selected?: boolean
      setIsFocusableByKeypress?: React.Dispatch<React.SetStateAction<boolean>>
      theme?: Theme
      trailingNode?: ReactNode
      trailingIcon?: MenuOptionIcon
    }
>

/**
 *
 * @deprecated Use new composable `MenuOption` component
 * with `leadingNode` or `trailingNode` for icons
 */
export const OptionIcon: VFC<ShowOptionIconArg> = ({
  icon,
  disabled,
  selected,
  theme: propTheme,
  ariaHidden,
}) => {
  const providedTheme = useTheme()

  if (!icon) {
    return null
  }

  const { visibility, glyph, color, useHypernova } = icon
  const theme = propTheme ?? providedTheme

  if (
    visibility === 'always' ||
    (visibility === 'disabled' && disabled) ||
    (visibility === 'selected' && selected)
  ) {
    return (
      <Icon
        glyph={glyph}
        color={
          color ??
          (selected ? Color[theme].Surface.A200 : Color[theme].Text.Default)
        }
        useHypernova={Boolean(useHypernova)}
        ariaHidden={ariaHidden}
      />
    )
  }

  return null
}
OptionIcon.displayName = 'Lapidary: OptionIcon ← MenuOptionComponent'

const MenuOptionComponent = <IdValue,>(
  {
    color,
    colorsEnabled,
    disabled,
    leadingAvatarProps,
    leadingIcon,
    highlightedByKeyboard = false,
    id,
    multiSelectType,
    onSelect,
    optionText,
    selected = false,
    setIsFocusableByKeypress,
    styleOverrides,
    theme: propTheme,
    trailingNode,
    trailingIcon,
    ...listItemProps
  }: MenuOptionProps<IdValue>,
  ref: Ref<HTMLLIElement>
) => {
  const [checkboxLabelId] = useState(uuidv4())
  const onKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Enter' && !disabled) {
      onSelect?.({ id, optionText })
    }
  }
  const providedTheme = useTheme()
  const theme = propTheme ?? providedTheme

  return (
    <li
      ref={ref}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled}
      css={[
        styles.menuOption({
          selected,
          theme,
          disabled,
          highlightedByKeyboard,
        }),
        styleOverrides,
      ]}
      // used with useListKeyboardNavigation when highlightOnFocus is true to prevent
      // unnecessarily highlighting first item in the list when a different item is clicked
      onMouseDown={() =>
        setIsFocusableByKeypress && setIsFocusableByKeypress(false)
      }
      onFocus={(e) => e.stopPropagation()}
      onClick={() => !disabled && onSelect?.({ id, optionText })}
      onKeyDown={onKeyDown}
      {...listItemProps}
    >
      {Boolean(multiSelectType) &&
        multiSelectType === MultiSelectType.checkbox && (
          <Checkbox
            checked={selected}
            disabled
            labedlledbyId={checkboxLabelId}
            onChange={() => null} // list item onClick event will fire instead
            css={styles.checkbox}
            tabIndex={-1}
            theme={theme}
          />
        )}
      {color && colorsEnabled && <ColorDotToken color={color} />}
      {leadingAvatarProps && leadingAvatarProps.name && (
        <Avatar
          fullName={leadingAvatarProps.name}
          src={leadingAvatarProps.fullUrl}
          size={24}
        />
      )}
      <OptionIcon
        icon={leadingIcon}
        disabled={disabled}
        selected={selected}
        ariaHidden
      />
      <span
        className="menu-option-text"
        css={styles.menuText}
        id={checkboxLabelId}
      >
        {optionText}
      </span>
      <OptionIcon
        icon={trailingIcon}
        disabled={disabled}
        selected={selected}
        ariaHidden
      />
      {Boolean(trailingNode) && (
        <div css={styles.trailingImage}>{trailingNode}</div>
      )}
    </li>
  )
}
MenuOptionComponent.displayName = 'Lapidary: MenuOptionComponent'

/**
 * @deprecated Use new composable `MenuOption` component
 */
export const DeprecatedMenuOption = forwardRef(MenuOptionComponent) as <
  IdValue,
>(
  p: MenuOptionProps<IdValue> & { ref?: Ref<HTMLLIElement> }
) => ReactElement
